import React, { Suspense } from "react";
import { Link } from "react-router-dom";
import "./Home.css";
import { Canvas } from "@react-three/fiber";
import CowboyModel from "../components/CowboyModel";
import Seo from "../components/Seo";

function Home() {
  return (
    <div className="home" data-theme="home">
      <Seo
        title="Residential & Commercial Painters in Kingwood – J3 Innovations"
        description="J3 Innovations is a painting contractor in Kingwood and Houston delivering interior, exterior, commercial, rental, and Airbnb painting with clean lines and careful prep."
        canonicalPath="/"
        image="/logo192.png"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "PaintingContractor",
          name: "J3 Innovations",
          areaServed: ["Kingwood", "Houston", "TX"],
          url: "https://www.j3innovations.com/",
          services: [
            "Residential Painting",
            "Commercial Painting",
            "Interior Painting",
            "Exterior Painting",
            "Rental Turnovers",
            "Airbnb Painting",
          ],
          description:
            "Painting contractor serving Kingwood and Houston with interior, exterior, commercial, rental, and Airbnb painting.",
        }}
      />
      <section className="hero" id="home">
        <div className="hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">
              Father–Son Painting &amp; Construction Team
            </p>
            <h1>
              Residential &amp; Commercial Painters in Kingwood – J3 Innovations
            </h1>
            <p className="subhead">
              Painting company in Kingwood serving homes, businesses, and
              rentals with 30+ years of experience. Interior and exterior
              painters who think like builders.
            </p>
            <p className="subhead">
              We plan the prep, protect your space, and deliver clean lines and
              durable finishes for homeowners, landlords, Airbnb hosts, and
              property managers.
            </p>
            <div className="cta-row">
              <Link to="/quote" className="btn primary">
                Request a Free Estimate
              </Link>
              <a href="sms:2818575307" className="btn ghost">
                Text Us Photos of Your Project
              </a>
            </div>
            <div className="swatch-strip">
              <span className="swatch swatch-brand" />
              <span className="swatch swatch-coral" />
              <span className="swatch swatch-sand" />
              <span className="swatch swatch-surface" />
            </div>
            <div className="hero-tags">
              <span>Kingwood painters · Houston area</span>
              <span>Prep-driven, durable finishes</span>
              <span>Respectful crews, clear schedules</span>
            </div>
            <div className="trust-bar">
              <span>20+ years experience</span>
              <span>Insured &amp; safety-minded</span>
              <span>Clean lines &amp; respectful crew</span>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-canvas">
              <Canvas camera={{ position: [0, 1.8, 7], fov: 55 }}>
                <ambientLight intensity={1} />
                <directionalLight position={[4, 6, 4]} intensity={1.4} />
                <Suspense fallback={null}>
                  <CowboyModel />
                </Suspense>
              </Canvas>
            </div>
          </div>
        </div>
      </section>

      <section className="services" id="services">
        <div className="section-header">
          <p className="eyebrow">Painting Services We Offer</p>
          <h2>
            Homes, rentals, and businesses—built on prep, protection, and clean
            lines.
          </h2>
          <p className="card-text">
            Full-service interior and exterior painting company in Kingwood
            serving residential, commercial, and fast-turn rentals across
            Houston.
          </p>
        </div>
        <div className="service-grid">
          <div className="service-card">
            <div className="card-top">
              <p className="label">Homes</p>
              <h3>Residential Painting</h3>
              <p className="card-text">
                Full interior and exterior house painting with careful prep and
                durable finishes.
              </p>
            </div>
            <Link to="/residential-painting" className="link">
              Explore Residential Painting →
            </Link>
          </div>

          <div className="service-card">
            <div className="card-top">
              <p className="label secondary">Commercial</p>
              <h3>Commercial Painting</h3>
              <p className="card-text">
                Offices, retail, multifamily, and HOA projects with schedules
                that respect tenants and customers.
              </p>
            </div>
            <Link to="/commercial-painting" className="link">
              Explore Commercial Painting →
            </Link>
          </div>

          <div className="service-card">
            <div className="card-top">
              <p className="label">Fast Flash</p>
              <h3>Rental &amp; Fast Flash Repaints</h3>
              <p className="card-text">
                Quick-turn painting for rentals and apartments with predictable
                timelines.
              </p>
            </div>
            <Link to="/rental-painting-fast-flash" className="link">
              Explore Fast Flash →
            </Link>
          </div>

          <div className="service-card">
            <div className="card-top">
              <p className="label secondary">Airbnb</p>
              <h3>Airbnb &amp; STR Painting</h3>
              <p className="card-text">
                Photo-ready walls and durable finishes that stand up to
                turnovers.
              </p>
            </div>
            <Link to="/airbnb-painting" className="link">
              Explore Airbnb Painting →
            </Link>
          </div>

          <div className="service-card">
            <div className="card-top">
              <p className="label">Interiors</p>
              <h3>Interior Painting &amp; Epoxy</h3>
              <p className="card-text">
                Walls, ceilings, trim, doors, plus epoxy and high-durability
                interior floor coatings.
              </p>
            </div>
            <Link to="/interior-painting" className="link">
              Explore Interior Painting →
            </Link>
          </div>

          <div className="service-card">
            <div className="card-top">
              <p className="label secondary">Exteriors</p>
              <h3>Exterior Painting</h3>
              <p className="card-text">
                Full exterior repaints, trim, doors, and fascia built for
                Houston weather.
              </p>
            </div>
            <Link to="/exterior-painting" className="link">
              Explore Exterior Painting →
            </Link>
          </div>
        </div>
      </section>

      <section className="services">
        <div className="section-header">
          <p className="eyebrow">Why Homeowners and Businesses Choose Us</p>
          <h2>Prep-driven painters in Kingwood who respect your space.</h2>
        </div>
        <div className="service-card">
          <ul>
            <li>30+ years of painting and construction experience.</li>
            <li>
              We think like builders: scopes, prep, and protection come first.
            </li>
            <li>Clean lines and details people actually see.</li>
            <li>Clear scopes and schedules with no surprise add-ons.</li>
            <li>Respectful crews that keep homes and job sites clean.</li>
          </ul>
        </div>
      </section>

      <section className="services">
        <div className="section-header">
          <p className="eyebrow">How Working With Us Works</p>
          <h2>Simple steps to a well-planned paint job.</h2>
        </div>
        <div className="service-card">
          <ol>
            <li>Reach out by call, text, or form.</li>
            <li>Share photos or schedule a walk-through.</li>
            <li>Receive a clear, written estimate and timeline.</li>
            <li>We prep, paint, and clean up with minimal disruption.</li>
            <li>Final walk-through and touch-ups before we call it done.</li>
          </ol>
          <p className="card-text">
            We communicate throughout and treat your space with respect.
          </p>
        </div>
      </section>

      <section className="services">
        <div className="section-header">
          <p className="eyebrow">Who We Serve</p>
          <h2>Residential, rentals, and commercial clients in Kingwood.</h2>
        </div>
        <div className="service-card">
          <ul>
            <li>Homeowners needing interior or exterior painting.</li>
            <li>Landlords and investors planning turnovers.</li>
            <li>
              Airbnb and short-term rental hosts needing photo-ready refreshes.
            </li>
            <li>
              Business owners and property managers with schedules to keep.
            </li>
          </ul>
        </div>
      </section>

      <section className="services">
        <div className="section-header">
          <p className="eyebrow">Service Areas</p>
          <h2>Painting company in Kingwood serving the Houston area.</h2>
          <p className="card-text">
            Based in Kingwood and working across Houston metro communities.
          </p>
        </div>
        <div className="service-card">
          <ul>
            <li>Kingwood</li>
            <li>The Woodlands</li>
            <li>Katy</li>
            <li>Sugar Land</li>
            <li>Pearland</li>
            <li>Memorial</li>
            <li>Spring</li>
            <li>Humble</li>
          </ul>
          <Link to="/house-painters" className="cta-inline">
            See more on our Service Areas page →
          </Link>
        </div>
      </section>

      <section className="services">
        <div className="section-header">
          <p className="eyebrow">Ready to Talk?</p>
          <h2>Fast responses, honest estimates, and clean finishes.</h2>
        </div>
        <div className="service-card">
          <p className="card-text">
            Tell us about your project and we’ll share a clear scope, schedule,
            and product plan.
          </p>
          <div className="cta-row">
            <Link to="/quote" className="btn primary">
              Request a Free Estimate
            </Link>
            <a href="sms:2818575307" className="btn ghost">
              Text Us Photos
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
